export const pdfToCanvasY = (y, height, pageHeight) => pageHeight - y - height;

const pickRectComponent = (rect = {}, upper, lower, fallback = 0) => {
  const value = rect?.[upper] ?? rect?.[lower] ?? fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number(fallback) || 0;
};

const applyTransform = (point, transform = []) => {
  const [a = 1, b = 0, c = 0, d = 1, e = 0, f = 0] = transform;
  const [x = 0, y = 0] = point;

  return [a * x + c * y + e, b * x + d * y + f];
};

const applyInverseTransform = (point, transform = []) => {
  const [a = 1, b = 0, c = 0, d = 1, e = 0, f = 0] = transform;
  const [x = 0, y = 0] = point;
  const det = a * d - b * c;

  if (!det) {
    return [x, y];
  }

  const tx = x - e;
  const ty = y - f;

  return [(d * tx - c * ty) / det, (-b * tx + a * ty) / det];
};

export const canvasDeltaToPdfDelta = (
  deltaX,
  deltaY,
  viewport,
  fallbackScale = 1,
  preserveMagnitude = false,
) => {
  if (hasViewportTransform(viewport)) {
    const origin = applyInverseTransform([0, 0], viewport.transform);
    const destination = applyInverseTransform(
      [deltaX, deltaY],
      viewport.transform,
    );

    const pdfDelta = {
      x: destination[0] - origin[0],
      y: destination[1] - origin[1],
    };

    if (!preserveMagnitude) return pdfDelta;

    const canvasMagnitude = Math.hypot(deltaX, deltaY);
    const pdfMagnitude = Math.hypot(pdfDelta.x, pdfDelta.y);
    const magnitudeScale = pdfMagnitude ? canvasMagnitude / pdfMagnitude : 1;

    return {
      x: pdfDelta.x * magnitudeScale,
      y: pdfDelta.y * magnitudeScale,
    };
  }

  const scale = Number(fallbackScale) || 1;
  return {
    x: deltaX / scale,
    y: -deltaY / scale,
  };
};

const clamp = (value, minimum, maximum) =>
  Math.min(Math.max(value, minimum), maximum);

export const resizeCanvasRect = (
  rect,
  deltaX,
  deltaY,
  direction,
  bounds,
  minimumSize = 5,
) => {
  const initialLeft = Number(rect?.left) || 0;
  const initialTop = Number(rect?.top) || 0;
  const initialRight = initialLeft + (Number(rect?.width) || 0);
  const initialBottom = initialTop + (Number(rect?.height) || 0);
  const minimumWidth = Math.max(Number(minimumSize) || 0, 0);
  const minimumHeight = minimumWidth;
  const boundsLeft = Number(bounds?.left) || 0;
  const boundsTop = Number(bounds?.top) || 0;
  const boundsRight = Number.isFinite(Number(bounds?.right))
    ? Number(bounds.right)
    : Number.POSITIVE_INFINITY;
  const boundsBottom = Number.isFinite(Number(bounds?.bottom))
    ? Number(bounds.bottom)
    : Number.POSITIVE_INFINITY;

  let left = initialLeft;
  let top = initialTop;
  let right = initialRight;
  let bottom = initialBottom;

  if (direction?.includes("w")) {
    left = clamp(initialLeft + deltaX, boundsLeft, initialRight - minimumWidth);
  }
  if (direction?.includes("e")) {
    right = clamp(
      initialRight + deltaX,
      initialLeft + minimumWidth,
      boundsRight,
    );
  }
  if (direction?.includes("n")) {
    top = clamp(initialTop + deltaY, boundsTop, initialBottom - minimumHeight);
  }
  if (direction?.includes("s")) {
    bottom = clamp(
      initialBottom + deltaY,
      initialTop + minimumHeight,
      boundsBottom,
    );
  }

  return {
    left,
    top,
    width: right - left,
    height: bottom - top,
  };
};

const hasViewportTransform = (viewport) =>
  Boolean(viewport && Array.isArray(viewport.transform));

const rectToPdfBounds = (rect = {}) => {
  const x = pickRectComponent(rect, "X", "x", 0);
  const y = pickRectComponent(rect, "Y", "y", 0);
  const width = pickRectComponent(rect, "W", "width", 0);
  const height = pickRectComponent(rect, "H", "height", 0);

  return {
    x1: x,
    y1: y,
    x2: x + width,
    y2: y + height,
    width,
    height,
  };
};

const viewportRectFromPdfBounds = (bounds, viewport) => {
  const p1 = applyTransform([bounds.x1, bounds.y1], viewport.transform);
  const p2 = applyTransform([bounds.x2, bounds.y2], viewport.transform);

  const left = Math.min(p1[0], p2[0]);
  const top = Math.min(p1[1], p2[1]);
  const width = Math.abs(p2[0] - p1[0]);
  const height = Math.abs(p2[1] - p1[1]);

  return { left, top, width, height };
};

export const pdfRectToCanvasRect = (
  rect,
  pageHeightOrViewport,
  scale = 1,
  pageXMin = 0,
  pageYMin = 0,
) => {
  if (hasViewportTransform(pageHeightOrViewport)) {
    return viewportRectFromPdfBounds(
      rectToPdfBounds(rect),
      pageHeightOrViewport,
    );
  }

  const pageHeight = pageHeightOrViewport;
  const x = pickRectComponent(rect, "X", "x", 0) - pageXMin;
  const y = pickRectComponent(rect, "Y", "y", 0) - pageYMin;
  const width = pickRectComponent(rect, "W", "width", 0);
  const height = pickRectComponent(rect, "H", "height", 0);

  return {
    left: x * scale,
    top: pdfToCanvasY(y, height, pageHeight) * scale,
    width: width * scale,
    height: height * scale,
  };
};

export const pdfPointsToCanvas = (
  x,
  y,
  width,
  height,
  scaleOrViewport = 1,
  dpi = 72,
  pageHeight = 0,
  pageXMin = 0,
  pageYMin = 0,
) => {
  if (hasViewportTransform(scaleOrViewport)) {
    return viewportRectFromPdfBounds(
      {
        x1: x,
        y1: y,
        x2: x + width,
        y2: y + height,
        width,
        height,
      },
      scaleOrViewport,
    );
  }

  const scale = scaleOrViewport;
  const pxPerPoint = dpi / 72;
  return {
    left: (x - pageXMin) * pxPerPoint * scale,
    top: (pageHeight - (y - pageYMin) - height) * pxPerPoint * scale,
    width: width * pxPerPoint * scale,
    height: height * pxPerPoint * scale,
  };
};

export const canvasToPdfPoints = (
  left,
  top,
  width,
  height,
  scaleOrViewport = 1,
  dpi = 72,
  pageHeight = 0,
  pageXMin = 0,
  pageYMin = 0,
) => {
  if (hasViewportTransform(scaleOrViewport)) {
    const p1 = applyInverseTransform([left, top], scaleOrViewport.transform);
    const p2 = applyInverseTransform(
      [left + width, top + height],
      scaleOrViewport.transform,
    );

    const pdfLeft = Math.min(p1[0], p2[0]);
    const pdfBottom = Math.min(p1[1], p2[1]);
    const pdfRight = Math.max(p1[0], p2[0]);
    const pdfTop = Math.max(p1[1], p2[1]);

    return {
      x: pdfLeft,
      y: pdfBottom,
      width: pdfRight - pdfLeft,
      height: pdfTop - pdfBottom,
    };
  }

  const scale = scaleOrViewport;
  const pxPerPoint = dpi / 72;
  const base = pxPerPoint * scale || 1;

  const pdfX = left / base + pageXMin;
  const pdfWidth = width / base;
  const pdfHeight = height / base;
  const pdfY = pageHeight - top / base - pdfHeight + pageYMin;

  return {
    x: pdfX,
    y: pdfY,
    width: pdfWidth,
    height: pdfHeight,
  };
};
